import { useState, useCallback } from 'react';
import { TourContext } from './TourContext';
import { TourOverlay } from './TourOverlay';

/**
 * Tour Provider - wraps the app and manages tour state
 */
export function TourProvider({ children }) {
  const [activeTour, setActiveTour] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [completedTours, setCompletedTours] = useState(() => {
    const saved = localStorage.getItem('microsafe_completed_tours');
    return saved ? JSON.parse(saved) : {};
  });

  const startTour = useCallback((tourId, steps) => {
    setActiveTour({ id: tourId, steps });
    setCurrentStep(0);
  }, []);

  const endTour = useCallback((markComplete = true) => {
    if (activeTour && markComplete) {
      const newCompleted = { ...completedTours, [activeTour.id]: true };
      setCompletedTours(newCompleted);
      localStorage.setItem('microsafe_completed_tours', JSON.stringify(newCompleted));
    }
    setActiveTour(null);
    setCurrentStep(0);
  }, [activeTour, completedTours]);

  const nextStep = useCallback(() => {
    if (activeTour && currentStep < activeTour.steps.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      endTour(true);
    }
  }, [activeTour, currentStep, endTour]);

  const prevStep = useCallback(() => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  }, [currentStep]);

  const isTourCompleted = useCallback((tourId) => {
    return completedTours[tourId] === true;
  }, [completedTours]);

  const resetTour = useCallback((tourId) => {
    const newCompleted = { ...completedTours };
    delete newCompleted[tourId];
    setCompletedTours(newCompleted);
    localStorage.setItem('microsafe_completed_tours', JSON.stringify(newCompleted));
  }, [completedTours]);

  return (
    <TourContext.Provider value={{
      activeTour,
      currentStep,
      startTour,
      endTour,
      nextStep,
      prevStep,
      isTourCompleted,
      resetTour
    }}>
      {children}
      {activeTour && <TourOverlay />}
    </TourContext.Provider>
  );
}

export default TourProvider;
